import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { stream } from "../api/axios";
import { ndjsonStream } from "../api/ndjson";
import { appendBlock, appendTextDelta, dropOptimisticTurn } from "./chatMessages";
import { useEvent } from "./EventContext";
import { useView } from "./ViewContext";

const ChatbotContext = createContext();

export function ChatbotProvider({ learningJourneyId, children }) {
    const [showChatbot, setShowChatbot] = useState(false);
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoadingBlock, setIsLoadingBlock] = useState(false);
    const [scrollTrigger, setScrollTrigger] = useState(0);
    const [activeInputBlock, setActiveInputBlock] = useState(null);
    const [failedTurn, setFailedTurn] = useState(null);
    const [streamStalledMs, setStreamStalledMs] = useState(0);

    // Timestamp of the last received chunk, updated in every branch of the
    // streaming loop below -- drives Stage 2's "taking longer than usual" caption.
    const lastChunkAtRef = useRef(null);

    const { emitEvent } = useEvent();
    const { view, readOnly, closedNoticeText } = useView();
    const placeholder = view?.chat?.placeholder ?? null;

    // Tracks the last view_id for which we applied view-driven popover state
    // (e.g. chat_open). Stays in a ref so it survives re-renders without
    // re-triggering the effect.
    const lastAppliedViewIdRef = useRef(null);

    useEffect(() => {
        if (!view) return;
        setShowChatbot(view.chat != null);

        // Only set initial messages if history exists
        if (view.view_data?.chat_history?.messages) {
            setMessages(view.view_data.chat_history.messages);
        }

        // Each view's chat_open fully describes the popover's initial state
        // on that view, but only on transition: subsequent polls of the same
        // view (or the user's open/close actions within it) must not be
        // overridden.
        if (view.view_id !== lastAppliedViewIdRef.current) {
            lastAppliedViewIdRef.current = view.view_id;
            setOpen(view.chat?.open ?? false);
        }
    }, [view, learningJourneyId]);

    useEffect(() => {
        if (isStreaming) return;

        const lastMessage = messages[messages.length - 1];
        const inputBlock = lastMessage?.blocks?.find(
            b => b.block_type === "request_user_input" && b.active
        );

        setActiveInputBlock(inputBlock ?? null);
    }, [messages, isStreaming]);

    // Stage 2 ("still thinking" / "taking longer than usual") derives from how
    // long it has been since the last chunk arrived, not from a fixed timer --
    // this only ticks while a stream is actually in flight.
    useEffect(() => {
        if (!isStreaming) {
            setStreamStalledMs(0);
            return;
        }

        const interval = setInterval(() => {
            if (lastChunkAtRef.current == null) return;
            setStreamStalledMs(Date.now() - lastChunkAtRef.current);
        }, 1000);

        return () => clearInterval(interval);
    }, [isStreaming]);

    // Nothing in the optimistic pair (the user's message, the assistant
    // placeholder) was persisted server-side, so leaving it on screen would
    // show a conversation that a reload silently contradicts.
    const rollbackFailedTurn = useCallback((body, failure) => {
        setMessages(dropOptimisticTurn);

        setFailedTurn({
            retryable: failure.retryable,
            userInput: body?.user_input ?? null,
        });
    }, []);

    const clearFailedTurn = useCallback(() => setFailedTurn(null), []);

    // The lower-level NDJSON-parsing/message-accumulation/control-dispatch reducer,
    // generalized over the request that starts the stream so any endpoint that
    // streams this same event shape (today: /workspace/chat/ and the PF quiz
    // option-submit endpoint) can drive the same Chatbot UI without duplicating it.
    const streamRequest = useCallback(async (url, body) => {
        setIsStreaming(true);
        setFailedTurn(null);
        lastChunkAtRef.current = Date.now();
        setStreamStalledMs(0);

        let failure = null;

        try {
            const response = await stream(url, JSON.stringify(body));

            for await (const chunk of ndjsonStream(response.body)) {
                lastChunkAtRef.current = Date.now();
                setStreamStalledMs(0);

                if (chunk.type === "error") {
                    // Server is still alive and told us the turn is dead. Same
                    // outcome as a dead socket below -- one state, one recovery.
                    failure = { retryable: chunk.retryable ?? true };
                    continue;
                }

                if (chunk.type === "control") {
                    emitEvent(chunk);
                    continue;
                }

                if (chunk.type === "text-loading") {
                    setIsLoadingBlock(true);
                    continue;
                }

                setIsLoadingBlock(false);

                if (chunk.type === "block") {
                    setMessages((prev) => appendBlock(prev, chunk));
                } else if (chunk.type === "message" && chunk.role === "assistant") {
                    setMessages((prev) => appendTextDelta(prev, chunk.delta));
                }
            }
        } catch (err) {
            // The socket died with the server none the wiser: wifi drop, proxy
            // timeout, backend restart, suspended tab. This branch is why the
            // backend `error` event alone is not sufficient.
            console.error("Stream error", err);
            failure = { retryable: true };
        } finally {
            setIsStreaming(false);
            setIsLoadingBlock(false);
            if (failure) rollbackFailedTurn(body, failure);
        }
    }, [emitEvent, rollbackFailedTurn]);

    const fetchChatResponse = useCallback((userInputObj) => {
        return streamRequest(
            "/workspace/chat/", { journey_id: learningJourneyId, user_input: userInputObj }
        );
    }, [streamRequest, learningJourneyId]);

    const triggerUserScroll = () => {
        setScrollTrigger(prev => prev + 1);
    };

    const sendStructuredResponse = useCallback((requestId, selectedOptionIds) => {
        const block = activeInputBlock;
        if (!block) return;

        setActiveInputBlock(null);

        const selectedTexts = block.options
            .filter(opt => selectedOptionIds.includes(opt.option_id))
            .map(opt => opt.text);

        const newUserMessage = {
            role: "user",
            blocks: [{ block_type: "user_input_response", text: selectedTexts }]
        };

        setMessages(prev => [...prev, newUserMessage, { role: "assistant", blocks: [] }]);

        fetchChatResponse({
            type: "request_user_input_response",
            request_id: requestId,
            selected_option_ids: selectedOptionIds,
        });
    }, [activeInputBlock, fetchChatResponse]);

    return (
        <ChatbotContext.Provider value={{
            messages,
            setMessages,
            showChatbot,
            setShowChatbot,
            open,
            setOpen,
            fetchChatResponse,
            streamRequest,
            isStreaming,
            isLoadingBlock,
            scrollTrigger,
            triggerUserScroll,
            activeInputBlock,
            dismissActiveInputBlock: () => setActiveInputBlock(null),
            sendStructuredResponse,
            readOnly,
            closedNoticeText,
            placeholder,
            failedTurn,
            clearFailedTurn,
            streamStalledMs,
        }}>
            {children}
        </ChatbotContext.Provider>
    );
}

export function useChatbot() {
    return useContext(ChatbotContext);
}
